/**
 * KraftStories Meta Pixel Event Bridge
 * ------------------------------------
 * Maps the theme's existing dataLayer events (see analytics-events.js and
 * gtm-datalayer.liquid) to Meta Pixel (fbq) events. No separate
 * instrumentation: whatever GA4 sees, Meta sees.
 *
 * Two event tiers (config from snippets/meta-pixel.liquid):
 *
 * ALWAYS SENT (the Shopify "Facebook & Instagram" app cannot see these):
 *   form_success  -> Lead                 (Formspark booking/quote forms)
 *   form_submit   -> trackCustom LeadSubmit (attempt, incl. failures)
 *   sign_up       -> CompleteRegistration (newsletter)
 *   phone_click   -> Contact
 *   email_click   -> Contact
 *   modal_open    -> trackCustom BookingModalOpen
 *   view_item     -> trackCustom ViewWorkshop (rich workshop/location params)
 *   page load     -> trackCustom PageSegment  (page_category + city params
 *                    for parameter-based custom audiences)
 *
 * ONLY WHEN standardEvents === true (theme setting; keep OFF while the
 * official Facebook & Instagram Shopify app is installed, it already sends
 * these via browser pixel + Conversions API):
 *   view_item      -> ViewContent
 *   add_to_cart    -> AddToCart
 *   begin_checkout -> InitiateCheckout
 *   search         -> Search
 *   (PageView fires from meta-pixel.liquid)
 *
 * Purchase is intentionally NOT handled here: the storefront theme cannot
 * run on Shopify checkout / thank-you pages. Purchase comes from the
 * Facebook & Instagram app (with CAPI + dedup) — do not add it elsewhere.
 */

(function () {
  'use strict';

  var cfg = window.__kraftMetaPixel;
  if (!cfg || !cfg.pixelId || typeof window.fbq !== 'function') return;

  var fbq = window.fbq;
  var standard = !!cfg.standardEvents;

  // ── Helpers ────────────────────────────────────────────────────────

  function eventId(prefix) {
    // Unique event_id: enables browser/server dedup if CAPI is added later.
    return (
      prefix + '.' + Date.now() + '.' + Math.random().toString(36).slice(2, 10)
    );
  }

  function segmentation() {
    var out = {};
    if (window.__kraft_page_category) out.page_category = window.__kraft_page_category;
    if (window.__kraft_page_location_city) out.page_location_city = window.__kraft_page_location_city;
    return out;
  }

  function num(v) {
    var n = parseFloat(v);
    return isNaN(n) ? 0 : Math.round(n * 100) / 100;
  }

  function itemsToContents(items) {
    return (items || []).map(function (i) {
      return {
        id: String(i.item_id || ''),
        quantity: i.quantity || 1,
        item_price: num(i.price),
      };
    });
  }

  function itemsToIds(items) {
    return (items || []).map(function (i) { return String(i.item_id || ''); });
  }

  function track(name, params, id) {
    try {
      fbq('track', name, params || {}, { eventID: id || eventId(name) });
    } catch (e) { /* never break the page for analytics */ }
  }

  function trackCustom(name, params, id) {
    try {
      fbq('trackCustom', name, params || {}, { eventID: id || eventId(name) });
    } catch (e) { /* noop */ }
  }

  // ── dataLayer event -> Meta mapping ────────────────────────────────

  function handle(entry) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return;
    var ev = entry.event;
    if (!ev) return;
    var ec = entry.ecommerce || {};
    var items = ec.items || [];
    var currency = ec.currency || window.__kraft_analytics_currency || 'USD';

    switch (ev) {
      // ── Ecommerce funnel ──────────────────────────────────────────
      case 'view_item': {
        var vcParams = Object.assign({
          content_type: 'product',
          content_ids: itemsToIds(items),
          content_name: items[0] ? items[0].item_name : '',
          content_category: items[0] ? items[0].item_category : '',
          value: num(ec.value),
          currency: currency,
        }, segmentation());
        if (standard) track('ViewContent', vcParams);
        // Rich custom event for granular audiences (always).
        trackCustom('ViewWorkshop', Object.assign({}, vcParams, {
          workshop_name: entry.workshop_name || '',
          booking_location: entry.booking_location || '',
          booking_location_name: entry.booking_location_name || '',
        }));
        break;
      }

      case 'add_to_cart':
        if (!standard) break;
        track('AddToCart', Object.assign({
          content_type: 'product',
          content_ids: itemsToIds(items),
          contents: itemsToContents(items),
          content_name: items[0] ? items[0].item_name : '',
          value: num(ec.value),
          currency: currency,
        }, segmentation()));
        break;

      case 'begin_checkout':
        if (!standard) break;
        track('InitiateCheckout', Object.assign({
          content_type: 'product',
          content_ids: itemsToIds(items),
          contents: itemsToContents(items),
          num_items: items.reduce(function (s, i) { return s + (i.quantity || 1); }, 0),
          value: num(ec.value),
          currency: currency,
        }, segmentation()));
        break;

      case 'search':
        if (!standard) break;
        track('Search', Object.assign({
          search_string: entry.search_term || '',
        }, segmentation()));
        break;

      // ── Leads & contact (always) ──────────────────────────────────
      case 'form_success':
        track('Lead', Object.assign({
          content_name: entry.form_name || 'form',
          content_category: entry.inquiry_type || entry.lead_type || '',
          lead_type: entry.lead_type || '',
          form_location: entry.location || '',
        }, segmentation()));
        break;

      case 'form_submit':
        trackCustom('LeadSubmit', Object.assign({
          content_name: entry.form_name || 'form',
          content_category: entry.inquiry_type || entry.lead_type || '',
          lead_type: entry.lead_type || '',
          form_location: entry.location || '',
        }, segmentation()));
        break;

      case 'sign_up':
        track('CompleteRegistration', Object.assign({
          content_name: entry.method || 'newsletter',
          status: true,
        }, segmentation()));
        break;

      case 'phone_click':
        track('Contact', Object.assign({
          content_name: 'phone',
        }, segmentation()));
        break;

      case 'email_click':
        track('Contact', Object.assign({
          content_name: 'email',
        }, segmentation()));
        break;

      case 'modal_open':
        trackCustom('BookingModalOpen', Object.assign({
          modal_id: entry.modal_id || '',
        }, segmentation()));
        break;

      default:
        break;
    }
  }

  // ── Wire up: replay past pushes, then intercept future ones ───────

  window.dataLayer = window.dataLayer || [];

  // Replay entries pushed before this (deferred) script ran — e.g. the
  // Liquid-rendered view_item on product pages.
  for (var i = 0; i < window.dataLayer.length; i += 1) {
    handle(window.dataLayer[i]);
  }

  var originalPush = window.dataLayer.push;
  window.dataLayer.push = function () {
    for (var j = 0; j < arguments.length; j += 1) {
      handle(arguments[j]);
    }
    return originalPush.apply(window.dataLayer, arguments);
  };

  // ── PageSegment: parameter-based audience seed on every page ──────
  trackCustom('PageSegment', Object.assign({
    page_path: window.location.pathname,
  }, segmentation()));
})();
